import { useState, useEffect, useRef } from "react";
import { completeTriviaRound, generateTriviaQuestions } from "../utils/api.js";
import { Ava } from "../atoms/Ava.jsx";

export function TriviaModal({persona, voterPhone, onClose, onXpEarned}) {
  const [phase, setPhase] = useState("loading"); // loading | playing | result
  const [questions, setQuestions] = useState([]);
  // v1.28.3 — server-issued round token. Required by /complete so the
  // server can re-score from the stored answer key instead of trusting
  // a client-submitted value. Null when the round came from the offline
  // fallback (see generateTriviaQuestions).
  const [roundToken, setRoundToken] = useState(null);
  const [qi, setQi] = useState(0); // current question index
  const [selected, setSelected] = useState(null); // chosen option index
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);
  const [timerKey, setTimerKey] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  // answers[] now carries BOTH the client-side review state and the
  // submission payload. `chosen` is the raw picked index (or -1 on
  // timeout) — this is what we POST to /complete. `correct` is the
  // optimistic view; authoritative correctness comes back from
  // /complete's response and could override this on a mismatch.
  const [answers, setAnswers] = useState([]);
  // v1.28.5 — disables the Claim button while /complete is in flight so
  // a double-tap can't fire two POSTs (the second would 409 as a replay
  // and surface a spurious "claim failed" toast).
  const [claiming, setClaiming] = useState(false);
  const timerRef = useRef(null);
  const pc = persona.accentColor;
  const TIMER = 20;

  useEffect(()=>{
    generateTriviaQuestions(persona, voterPhone).then(({questions: qs, roundToken: rt})=>{
      setQuestions(qs || []);
      setRoundToken(rt);
      setPhase("playing");
    });
  },[]);

  const clearTimer = () => { if(timerRef.current) clearTimeout(timerRef.current); };

  useEffect(()=>{
    if(phase!=="playing"||revealed) return;
    clearTimer();
    timerRef.current = setTimeout(()=>{ setTimedOut(true); handleReveal(null); }, TIMER*1000);
    return clearTimer;
  },[qi, phase, revealed]);

  const handleReveal = (idx) => {
    clearTimer();
    setSelected(idx);
    setRevealed(true);
    const q = questions[qi];
    const correct = idx===q.correct;
    if(correct) setScore(s=>s+1);
    // `chosen: -1` on timeout — matches how the server treats a missing
    // answer (never matches any real correct index, counts as wrong).
    setAnswers(a=>[...a,{correct,chosen:typeof idx==="number"?idx:-1,answer:q.correct}]);
  };

  const nextQuestion = () => {
    if(qi+1>=questions.length){ setPhase("result"); return; }
    setQi(i=>i+1);
    setSelected(null);
    setRevealed(false);
    setTimedOut(false);
    setTimerKey(k=>k+1);
  };

  if(phase==="loading") return (
    <div style={{position:"fixed",inset:0,background:"#000000cc",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{textAlign:"center",color:"#f1f5f9"}}>
        <div style={{fontSize:36,marginBottom:12,animation:"bop 1s ease infinite"}}>🧠</div>
        <div style={{fontFamily:"Georgia,serif",fontSize:16,marginBottom:6}}>Generating questions…</div>
        <div style={{color:"#475569",fontSize:12}}>Powered by AI — questions are unique each time</div>
      </div>
    </div>
  );

  if(phase==="result") {
    const xp = score;
    return (
      <div style={{position:"fixed",inset:0,background:"#000000cc",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
        <div style={{background:"#0d1724",border:`1px solid ${pc}40`,borderRadius:20,padding:"32px 24px",maxWidth:380,width:"100%",textAlign:"center",animation:"popIn .4s ease"}}>
          <div style={{fontSize:48,marginBottom:10}}>{score===5?"🏆":score>=3?"🎖️":score>=1?"👍":"😅"}</div>
          <h2 style={{color:"#f1f5f9",fontSize:22,fontWeight:900,fontFamily:"Georgia,serif",margin:"0 0 6px"}}>
            {score===5?"Perfect Score!":score>=3?"Well Done!":score>=1?"Not Bad!":"Better Luck Next Time"}
          </h2>
          <div style={{fontSize:36,fontWeight:900,color:pc,fontFamily:"Georgia,serif",margin:"10px 0"}}>
            {score} / {questions.length}
          </div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:7,marginBottom:20,padding:"10px 16px",background:"#f59e0b12",border:"1px solid #f59e0b30",borderRadius:10}}>
            <span style={{fontSize:18}}></span>
            <span style={{color:"#f1f5f9",fontWeight:700,fontSize:15}}>+{xp} XP earned</span>
          </div>
          {/* Answer review */}
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:22,textAlign:"left"}}>
            {answers.map((a,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:a.correct?"#22c55e0d":"#ef44440d",border:`1px solid ${a.correct?"#22c55e25":"#ef444425"}`,borderRadius:7}}>
                <span style={{fontSize:14}}>{a.correct?"✅":"❌"}</span>
                <span style={{color:"#94a3b8",fontSize:12,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>Q{i+1}: {questions[i]?.q.slice(0,40)}…</span>
                <span style={{fontSize:10,color:a.correct?"#22c55e":"#ef4444",fontWeight:700,flexShrink:0}}>{a.correct?"✓ Correct":"✗ Wrong"}</span>
              </div>
            ))}
          </div>
          <button disabled={claiming} onClick={async ()=>{
            // v1.28.5 — pass the WHOLE server response (or null) to
            // onXpEarned. The useAppState handler uses the returned
            // XpState verbatim when present, so the UI is never stale
            // AND a failed /complete is visible to the user as an error
            // toast instead of the "XP rolled back" mystery it was.
            //
            // A null roundToken (offline fallback) still posts nothing,
            // yields null, and the parent surfaces the error — which is
            // correct: server never saw the round, so no XP can be
            // awarded.
            if (claiming) return;
            setClaiming(true);
            const picks = answers.map(a => (typeof a.chosen === "number" ? a.chosen : -1));
            let serverResult = null;
            try {
              serverResult = roundToken
                ? await completeTriviaRound(persona.id, roundToken, picks)
                : null;
            } catch {
              serverResult = null;
            }
            onXpEarned(serverResult);
            onClose();
          }} style={{width:"100%",padding:"13px",background:`linear-gradient(135deg,${pc},${pc}bb)`,color:"#fff",border:"none",borderRadius:11,fontSize:14,fontWeight:700,cursor:claiming?"wait":"pointer",fontFamily:"inherit",opacity:claiming?0.6:1}}>
            {claiming ? "Saving…" : xp === 0 ? "Try again →" : `Claim ${xp} XP →`}
          </button>
        </div>
      </div>
    );
  }

  const q = questions[qi];
  if(!q) return null;

  const optionLabels = ["A","B","C","D"];
  const diffColor = q.difficulty==="hard"?"#ef4444":q.difficulty==="medium"?"#f59e0b":"#22c55e";

  return (
    <div style={{position:"fixed",inset:0,background:"#000000ee",zIndex:1000,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#0d1724",border:`1px solid ${pc}35`,borderRadius:20,padding:24,maxWidth:460,width:"100%",animation:"fadeUp .25s ease"}}>
        {/* Header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <Ava ini={persona.avatar} color={pc} size={30} photoUrl={persona.photoUrl}/>
            <span style={{color:"#94a3b8",fontSize:12,fontWeight:600}}>{persona.name} Trivia</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:10,fontWeight:700,color:diffColor,textTransform:"uppercase",letterSpacing:"0.08em"}}>{q.difficulty}</span>
            <span style={{color:"#475569",fontSize:12}}>{qi+1}/{questions.length}</span>
          </div>
        </div>

        {/* Timer bar */}
        <div style={{height:3,background:"#1e2d45",borderRadius:2,marginBottom:18,overflow:"hidden"}}>
          {!revealed && (
            <div key={timerKey} style={{height:"100%",background:pc,borderRadius:2,
              animation:`timerShrink ${TIMER}s linear forwards`}}/>
          )}
        </div>

        {/* Score dots */}
        <div style={{display:"flex",gap:5,justifyContent:"center",marginBottom:18}}>
          {questions.map((_,i)=>(
            <div key={i} style={{width:8,height:8,borderRadius:"50%",
              background:i<answers.length?(answers[i].correct?"#22c55e":"#ef4444"):i===qi?pc:"#1e2d45"}}/>
          ))}
        </div>

        {/* Question */}
        <div style={{background:"#090e1a",border:"1px solid #1e2d45",borderRadius:12,padding:"16px 14px",marginBottom:16,minHeight:72,display:"flex",alignItems:"center"}}>
          <p style={{color:"#f1f5f9",fontSize:15,fontWeight:700,fontFamily:"Georgia,serif",lineHeight:1.5,margin:0}}>{q.q}</p>
        </div>

        {/* Timed out notice */}
        {timedOut && <div style={{textAlign:"center",color:"#f59e0b",fontSize:12,marginBottom:10,fontWeight:600}}>⏱ Time's up!</div>}

        {/* Options */}
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:18}}>
          {q.options.map((opt,i)=>{
            const isCorrect = i===q.correct;
            const isChosen = i===selected;
            let bg="#0d1724", border="1px solid #1e2d45", color="#cbd5e1";
            if(revealed){
              if(isCorrect){bg="#22c55e18";border="1px solid #22c55e60";color="#22c55e";}
              else if(isChosen&&!isCorrect){bg="#ef444418";border="1px solid #ef444460";color="#ef4444";}
            } else if(isChosen){bg=`${pc}18`;border=`1px solid ${pc}60`;color="#f1f5f9";}
            return (
              <button key={i} onClick={()=>!revealed&&handleReveal(i)} disabled={revealed}
                style={{padding:"11px 14px",background:bg,border,borderRadius:10,color,fontSize:13,cursor:revealed?"default":"pointer",fontFamily:"inherit",textAlign:"left",display:"flex",alignItems:"center",gap:10,fontWeight:isChosen||isCorrect&&revealed?700:400,transition:"all .15s"}}>
                <span style={{width:22,height:22,borderRadius:"50%",background:"#1e2d45",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0,color:"#64748b"}}>{optionLabels[i]}</span>
                {opt}
                {revealed&&isCorrect&&<span style={{marginLeft:"auto",fontSize:14}}>✓</span>}
                {revealed&&isChosen&&!isCorrect&&<span style={{marginLeft:"auto",fontSize:14}}>✗</span>}
              </button>
            );
          })}
        </div>

        {revealed && (
          <button onClick={nextQuestion} style={{width:"100%",padding:"12px",background:`linear-gradient(135deg,${pc},${pc}bb)`,color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",animation:"slideUp .2s ease"}}>
            {qi+1>=questions.length?"See Results →":"Next Question →"}
          </button>
        )}
      </div>
    </div>
  );
}

export default TriviaModal;
